import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { Notice, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

export default function NoticesTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<Notice[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<Notice> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<Notice>(`SELECT * FROM notices WHERE institution_eiin = ? ORDER BY id DESC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('notices', (editing as any).id, rec)
      else await d1Insert('notices', rec)
      onStatus({ type: 'success', text: 'Notice saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  const toggleActive = async (row: Notice) => {
    try {
      const newStatus = row.is_active === 0 ? 1 : 0
      await d1Update('notices', row.id!, { is_active: newStatus })
      onStatus({ type: 'success', text: `Notice ${newStatus ? 'activated' : 'deactivated'}!` })
      load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Notices" icon="📢"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'title_bn', label: 'Title (BN)', width: '220px' },
        { key: 'published_date', label: 'Date', width: '120px' },
        { key: 'category', label: 'Category', width: '90px' },
        { key: 'is_important', label: 'Important', width: '80px', render: (v) => v ? '🔴 Yes' : '—' },
        {
          key: 'is_active', label: 'Status', width: '100px',
          render: (v, row) => (
            <button
              onClick={(e) => { e.stopPropagation(); toggleActive(row) }}
              style={{
                padding: '3px 10px', borderRadius: '12px', border: 'none', fontWeight: 700, fontSize: '11px', cursor: 'pointer',
                background: v === 0 ? '#fee2e2' : '#dcfce7',
                color: v === 0 ? '#dc2626' : '#16a34a'
              }}
            >
              {v === 0 ? '🔴 Deactive' : '🟢 Active'}
            </button>
          )
        }
      ]}
      formFields={[
        { key: 'title_bn', label: 'Title (BN)', required: true },
        { key: 'title_en', label: 'Title (EN)', required: true },
        { key: 'published_date', label: 'Published Date', required: true },
        { key: 'category', label: 'Category', type: 'select', options: [
          { value: 'general', label: 'general' },
          { value: 'exam', label: 'exam' },
          { value: 'sports', label: 'sports' },
          { value: 'cultural', label: 'cultural' },
          { value: 'academic', label: 'academic' }
        ]},
        { key: 'is_important', label: 'Important?', type: 'select', options: [
          { value: '0', label: 'No' },
          { value: '1', label: 'Yes' }
        ]},
        { key: 'is_active', label: 'Status', type: 'select', options: [
          { value: '1', label: '🟢 Active' },
          { value: '0', label: '🔴 Deactive' }
        ]},
        { key: 'pdf_url', label: 'PDF Document / Notice Attachment', type: 'image', folder: 'notices/pdf' },
        { key: 'content_bn', label: 'Content (BN)', type: 'textarea' },
        { key: 'content_en', label: 'Content (EN)', type: 'textarea' }
      ]}
      onAdd={() => setEditing({ title_bn: '', title_en: '', published_date: '', category: 'general', is_important: 0, is_active: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('notices', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
